/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Zalify has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
(function (w) {
  /* customToggleVariant callback */
  let isInitialLoad = true;

  function scrollToTop() {
    if (window.__preventVariantScrollToTop) return;
    if (!window.location.pathname.includes('/products/')) return;
    window.scrollTo(0, 0);
    requestAnimationFrame(() => {
      if (window.__preventVariantScrollToTop) return;
      window.scrollTo(0, 0);
    });
  }

  window.customToggleVariant = function (id) {
    if (!isInitialLoad) {
      scrollToTop();
    }
    toggleCombinationImg(id);
    if (!isInitialLoad) {
      scrollToTop();
    }
    isInitialLoad = false;
  };

  document.addEventListener("DOMContentLoaded", () => {
    // 获取默认id
    const productData = document.querySelector('#product-data');
    const selectedVariantId = productData?.getAttribute('data-selected-variant-id');
    if (selectedVariantId) {
      toggleCombinationImg(selectedVariantId);
    }
    setTimeout(() => {
      isInitialLoad = false;
    }, 500);
  });
  
  // 组合option切换：同步过滤主图轮播（橱窗图）和缩略图
  // 注意：customToggleVariant 在 variant:changed 事件之前被调用，
  // 主题的 ProductMedia._onVariantChanged 会在事件后清掉所有 is-filtered，
  // 所以必须用 setTimeout 延迟到主题事件处理器执行完毕后再操作。
  function toggleCombinationImg(id) {
    try {
      const currentVariant = String(id);

      // 获取当前 variant 对应的 media-ids
      const currentInput = document.querySelector(`input[name='media-ids'][data-variant-id='${currentVariant}']`);
      if (!currentInput) return;

      const currentMediaIds = currentInput.dataset.mediaIds
        .split(',')
        .map(id => id.trim())
        .filter(id => id !== '');

      // 提取有效的数字 ID
      const validMediaIds = currentMediaIds.filter(id => /^\d+$/.test(id));

      // 如果当前 SKU 根本没有绑定 sku_extra_combination_img（没有纯数字的 media_id）
      // 直接 return，不执行任何拦截，把控制权完全交回给主题的默认逻辑
      // 主题会自动清理 is-filtered 并正确跳转到 featured_media
      if (validMediaIds.length === 0) {
        return;
      }

      // 收集所有 SKU 绑定过的纯数字图片 ID（即"有归属"的图片）
      const allInputs = document.querySelectorAll("input[name='media-ids']");
      const boundMediaIds = new Set(
        Array.from(allInputs)
          .flatMap(input => input.dataset.mediaIds.split(',').map(id => id.trim()))
          .filter(id => id !== '' && /^\d+$/.test(id))
      );

      // 延迟执行，确保在主题 variant:changed 事件处理器之后运行
      setTimeout(() => {
        // 过滤规则：
        // - 当前 SKU 绑定的图片 → 显示
        // - 其他 SKU 绑定的图片（在 boundMediaIds 中但不在 currentMediaIds 中）→ 隐藏
        // - 没有被任何 SKU 绑定的公共图片 → 显示
        document.querySelectorAll('[data-media-id]').forEach(item => {
          const mediaId = String(item.dataset.mediaId);
          const isBoundToOtherSku = boundMediaIds.has(mediaId) && !currentMediaIds.includes(mediaId);
          item.classList.toggle('is-filtered', isBoundToOtherSku);
        });

        // 通知 Flickity 重新加载 cells（cellSelector 排除 .is-filtered）
        const productMedia = document.querySelector('product-media');
        if (productMedia && productMedia.mainCarousel) {
          productMedia.mainCarousel.reload();
          // 跳转到绑定的第一张图
          productMedia.mainCarousel.select(`[data-media-id="${validMediaIds[0]}"]`);
        }
        if (!isInitialLoad) {
          scrollToTop();
        }
      }, 0);
    } catch (e) {
      console.error(e);
    }
  }

  w.getExpectTime = function (day_number) {
    try {
      let date = new Date();
      date.setDate(date.getDate() + day_number);
      function zeroFill(i) {
        if (i >= 0 && i <= 9) {
          return "0" + i;
        } else {
          return i;
        }
      }
      const month = zeroFill(date.getMonth() + 1);
      const day = zeroFill(date.getDate());
      let englist_month = "Jan.";
      if (month == "02") {
        englist_month = "Feb.";
      } else if (month == "03") {
        englist_month = "Mar.";
      } else if (month == "04") {
        englist_month = "Apr.";
      } else if (month == "05") {
        englist_month = "May.";
      } else if (month == "06") {
        englist_month = "Jun.";
      } else if (month == "07") {
        englist_month = "Jul.";
      } else if (month == "08") {
        englist_month = "Aug.";
      } else if (month == "09") {
        englist_month = "Sept.";
      } else if (month === 10) {
        englist_month = "Oct.";
      } else if (month === 11) {
        englist_month = "Nov.";
      } else if (month === 12) {
        englist_month = "Dec.";
      }
      const curTime = englist_month + day;
      console.log(curTime, "getExpectTime");
      return curTime;
    } catch (error) {
      console.log(error);
    }
  };

  w.customAddToCartEvent = function (product) {
    /* pinterest*/
    // pintrk("track", "addtocart", {
    //   value: product.original_price,
    //   order_quantity: 1,
    //   currency: Shopify.currency.active || "USD",
    //   line_items: [
    //     {
    //       product_name: product.name,
    //       product_id: product.id,
    //       product_price: product.original_price,
    //       product_quantity: product.quantity,
    //     },
    //   ],
    // });
  };
  /* add to cart event */
  const originalFetch = window.fetch;
  w.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    if (args[0].includes("/cart/add.js")) {
      const responseClone = response.clone();
      try {
        const data = await responseClone.json();
        w.customAddToCartEvent(data);
      } catch (error) {
        console.error("Failed to parse response:", error);
      }
    }
    return response;
  };

  document.addEventListener("DOMContentLoaded", () => {
    /* menu click event */
    document.addEventListener('click', function (e) {
      const menuLink = e.target.querySelector('span.header__linklist-link');
      if (menuLink) {
        const menuId = menuLink.getAttribute('data-aria-controls');
        if (!menuId) return;
        const isExpanded = menuLink.getAttribute('data-aria-expanded') === 'true';
        document.querySelectorAll('.header__linklist-link[data-aria-expanded="true"]').forEach(item => {
          if (item !== menuLink) {
            item.setAttribute('data-aria-expanded', 'false');
            const itemMenu = document.getElementById(item.getAttribute('data-aria-controls'));
            if (itemMenu) itemMenu.hidden = true;
          }
        });
        menuLink.setAttribute('data-aria-expanded', !isExpanded);
        const targetMenu = document.getElementById(menuId);
        if (targetMenu) {
          targetMenu.hidden = isExpanded;
        }
        return;
      }
      if (!e.target.closest('.header__linklist-item')) {
        document.querySelectorAll('.header__linklist-link[data-aria-expanded="true"]').forEach(item => {
          item.setAttribute('data-aria-expanded', 'false');
          const menu = document.getElementById(item.getAttribute('data-aria-controls'));
          if (menu) menu.hidden = true;
        });
      }
    });


    // 商品倒计时功能
    function initFlashSaleCountdown() {
      const productItems = document.querySelectorAll('product-item[data-flash-sale-countdown]');

      productItems.forEach(item => {
        const endTimeStr = item.dataset.flashSaleCountdown;
        if (!endTimeStr) return;

        const countdownText = item.querySelector('.countdown-text');
        if (!countdownText) return;

        const endTime = new Date(endTimeStr).getTime();

        const now = Date.now();

        if (endTime > now) {
          item.querySelector('.flash-sale-countdown').classList.add('active');
          const updateCountdown = () => {
            const now = Date.now();
            const timeLeft = endTime - now;

            if (timeLeft <= 0) {
              item.querySelector('.flash-sale-countdown').classList.remove('active');
              return;
            }

            const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
            const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

            const formatNumber = (num) => num.toString().padStart(2, '0');
            let timeString = '';
            if (days > 0) {
              const [d1, d2] = formatNumber(days).split('');
              timeString += `<span>${d1}</span><span>${d2}</span><span class='time-tips'>D:</span> `;
            }
            const [h1, h2] = formatNumber(hours).split('');
            const [m1, m2] = formatNumber(minutes).split('');
            const [s1, s2] = formatNumber(seconds).split('');
            timeString += `<span>${h1}</span><span>${h2}</span><span class='time-tips'>H:</span> `;
            timeString += `<span>${m1}</span><span>${m2}</span><span class='time-tips'>M:</span> `;
            timeString += `<span>${s1}</span><span>${s2}</span><span class='time-tips'>S</span>`;
            countdownText.innerHTML = timeString;
          };

          updateCountdown();
          setInterval(updateCountdown, 1000);
        } else {
          item.querySelector('.flash-sale-countdown').remove();
        }
      });
    }

    initFlashSaleCountdown();
    document.addEventListener('facet:updated', initFlashSaleCountdown);
  });

  /* breadcrumb */
  try {
    const referer = document.referrer;
    const refererHandle = referer.split('/collections/');
    const allProductsDom = document.querySelector('.breadcrumb__item[data-breadcrumb-handle="all-products"]');
    if (refererHandle.length > 1) {
      const targetDom = document.querySelector('.breadcrumb__item[data-breadcrumb-handle=' + refererHandle[1] + ']');
      if (targetDom) {
        targetDom.style.display = 'flex';
      } else {
        !!allProductsDom && (allProductsDom.style.display = 'flex')
      }
    } else {
      !!allProductsDom && (allProductsDom.style.display = 'flex');
    }
    const breadcrumbDom = document.querySelector('.breadcrumb');
    if (breadcrumbDom) breadcrumbDom.style.visibility = 'visible';
  } catch (e) {
    console.log(e);
  }

  /* Highlight text animation observer */
  function initHighlightText() {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 }
      );

      document.querySelectorAll('.highlight-text, [is="highlight-text"]').forEach((el) => {
        observer.observe(el);
      });
    } else {
      document.querySelectorAll('.highlight-text, [is="highlight-text"]').forEach((el) => {
        el.classList.add('animate');
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHighlightText);
  } else {
    initHighlightText();
  }

  document.addEventListener('shopify:section:load', initHighlightText);
  document.addEventListener('facet:updated', initHighlightText);

  /* Collection Infinite Scroll Component */
  class CollectionInfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this._onIntersection = this._onIntersection.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._observer = null;
      this._isLoading = false;
      this._hasScrolledDown = false;
      this._lastScrollY = window.scrollY;
    }

    connectedCallback() {
      this.loader = this.querySelector('.collection-infinite-scroll__loader');
      this.sentinel = this.querySelector('.collection-infinite-scroll__sentinel');
      this.endMessage = this.querySelector('.collection-infinite-scroll__end-message');
      this.nextUrl = this.getAttribute('data-next-url');

      if (!this.nextUrl || !this.sentinel) {
        if (this.loader) {
          this.loader.hidden = true;
          this.loader.style.display = 'none';
        }
        if (this.endMessage) {
          this.endMessage.hidden = false;
          this.endMessage.style.display = 'flex';
        }
        return;
      }

      // 监听用户滚动，确认用户产生向下滚动行为
      window.addEventListener('scroll', this._onScroll, { passive: true });

      // 延迟初始化 observer，避开首屏图片未撑开时的初始相交误判
      setTimeout(() => {
        this._setupObserver();
      }, 300);
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      window.removeEventListener('scroll', this._onScroll);
    }

    _onScroll() {
      const currentScrollY = window.scrollY;
      // 用户确实向下滚动，且距离顶部有一定位移时激活
      if (currentScrollY > this._lastScrollY && currentScrollY > 80) {
        this._hasScrolledDown = true;
      }
      this._lastScrollY = currentScrollY;
    }

    _setupObserver() {
      if (!('IntersectionObserver' in window)) {
        return;
      }

      // rootMargin 设为 0px 0px -40px 0px，杜绝提前在视口外偷跑
      // 只有当列表底部的哨兵元素真正进入屏幕视口 40px 时才触发
      this._observer = new IntersectionObserver(this._onIntersection, {
        root: null,
        rootMargin: '0px 0px -40px 0px',
        threshold: 0,
      });

      if (this.sentinel) {
        this._observer.observe(this.sentinel);
      }
    }

    _onIntersection(entries) {
      for (const entry of entries) {
        // 必须在哨兵真正进入视口内，且用户处于主动向下滚动时才触发
        if (entry.isIntersecting && this._hasScrolledDown && !this._isLoading && this.nextUrl) {
          this._loadNextPage();
          break;
        }
      }
    }

    async _loadNextPage() {
      if (this._isLoading || !this.nextUrl) return;
      this._isLoading = true;

      if (this.loader) {
        this.loader.hidden = false;
        this.loader.style.display = 'flex';
      }

      try {
        const fetchUrl = new URL(this.nextUrl, window.location.origin);
        const facetElement = this.closest('product-facet');
        const sectionId = facetElement ? facetElement.getAttribute('section-id') : null;
        if (sectionId) {
          fetchUrl.searchParams.set('section_id', sectionId);
        }

        // 核心：无论接口多快，增加 500ms 最小动画展示保底，确保用户肉眼能清晰看到转圈动效和 Loading 文字
        const [response] = await Promise.all([
          fetch(fetchUrl.toString()),
          new Promise((resolve) => setTimeout(resolve, 500)),
        ]);
        if (!response.ok) throw new Error(`HTTP error ${response.status}`);

        const html = await response.text();
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Extract and append new products and cards
        const newProductList = tempDiv.querySelector('.product-list__inner');
        const currentProductList = document.querySelector('.product-facet__product-list .product-list__inner');

        if (newProductList && currentProductList) {
          const newItems = Array.from(newProductList.children);
          newItems.forEach((child) => {
            // Remove reveal attribute and force opacity 1
            child.removeAttribute('reveal');
            child.style.opacity = '1';
            currentProductList.appendChild(child);
          });
        }

        // Check for subsequent page
        const newInfiniteScroll = tempDiv.querySelector('collection-infinite-scroll');
        const nextUrlCandidate = newInfiniteScroll ? newInfiniteScroll.getAttribute('data-next-url') : null;

        if (nextUrlCandidate && nextUrlCandidate !== this.nextUrl) {
          this.nextUrl = nextUrlCandidate;
          this.setAttribute('data-next-url', this.nextUrl);
          this.setAttribute('data-current-page', newInfiniteScroll.getAttribute('data-current-page') || '');
        } else {
          // Reached end of collection
          this.nextUrl = null;
          this.removeAttribute('data-next-url');
          if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
          }
          if (this.sentinel) {
            this.sentinel.remove();
          }
          if (this.endMessage) {
            this.endMessage.hidden = false;
            this.endMessage.style.display = 'flex';
          }
        }

        this._onContentUpdated();
      } catch (error) {
        console.error('Failed to load next page products:', error);
      } finally {
        this._isLoading = false;
        if (this.loader) {
          this.loader.hidden = true;
          this.loader.style.display = 'none';
        }
      }
    }

    _onContentUpdated() {
      // Dispatch facet:updated for highlight text and other listeners
      document.dispatchEvent(new CustomEvent('facet:updated'));

      // Re-trigger Judge.me preview badge rendering if present
      if (window.jdgm && typeof window.jdgm.customizeBadges === 'function') {
        try {
          window.jdgm.customizeBadges();
        } catch (e) {
          console.error(e);
        }
      }
    }
  }

  if (!window.customElements.get('collection-infinite-scroll')) {
    window.customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
  }

})(window);
